import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { useToast } from 'app/components/ui/use-toast';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from 'app/components/ui/form';
import { useLoginSlice } from '../slice';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const ResetPasswordForm = () => {
  const form = useForm();
  const { toast } = useToast();
  const { useResetPasswordMutation } = useLoginSlice();
  const [resetPassword, { isLoading, isError, isSuccess, error, data }] =
    useResetPasswordMutation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const handleResetPassword = async (values: any) => {
    if (!token) {
      toast({
        variant: 'destructive',
        description: 'Invalid or missing token.',
      });
      return;
    }
    if (!values.password || !values.confirmPassword) {
      toast({
        variant: 'destructive',
        description: 'Please fill in all fields.',
      });
      return;
    }
    if (values.password !== values.confirmPassword) {
      toast({
        variant: 'destructive',
        description: 'Passwords do not match.',
      });
      return;
    }
    resetPassword({ token, password: values.password });
  };

  useEffect(() => {
    if (isSuccess && data) {
      toast({
        variant: 'success',
        description: data.message || 'Password reset successful.',
      });
      form.reset();
      navigate('/login');
    }
    if (isError && error) {
      toast({
        variant: 'destructive',
        description: (error as any)?.data?.error || 'Failed to reset password.',
      });
    }
  }, [isSuccess, isError, data, error, toast, form]);

  return (
    <FormProvider {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(handleResetPassword)}
      >
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm New Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" className="w-full mt-2" disabled={isLoading}>
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
        {isSuccess && (
          <div className="text-green-600 text-sm text-center">
            Password reset successful. You can now{' '}
            <a href="/login" className="underline">
              login
            </a>
            .
          </div>
        )}
        {isError && (
          <div className="text-red-600 text-sm text-center">
            {(error as any)?.data?.error || 'Failed to reset password.'}
          </div>
        )}
      </form>
    </FormProvider>
  );
};
